import React from 'react'
import "./createProductModalStyles.css"
import { Input } from "../../modelsTesting"
import { FaArrowRight } from "react-icons/fa6";
import { LuSave, LuUpload, LuX, LuZap } from "react-icons/lu"
import { buildDefaultImageLabel, createLocalImagePreview, extractImageUrl, normalizeSkuPart, readFileAsDataUrl, stripFileExtension } from "./imageUtils";

function parseMoneyToCents(value) {
    const normalizedValue = String(value ?? "").trim().replace(/[^0-9.-]/g, "");

    if (!normalizedValue) {
        return NaN;
    }

    const parsedValue = Number(normalizedValue);
    if (Number.isNaN(parsedValue)) {
        return NaN;
    }

    return Math.round(parsedValue * 100);
}

function parseNumberOrZero(value) {
    if (String(value ?? "").trim() === "") {
        return 0;
    }

    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? NaN : parsedValue;
}

function parseRequiredNumber(value) {
    if (String(value ?? "").trim() === "") {
        return NaN;
    }

    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? NaN : parsedValue;
}

function sanitizeIntegerValue(value) {
    if (value === null || value === undefined) {
        return "";
    }

    const stringValue = String(value);
    if (stringValue.trim() === "") {
        return "";
    }

    return stringValue.replace(/\D/g, "");
}

function sanitizeSignedIntegerValue(value) {
    if (value === null || value === undefined) {
        return "";
    }

    const stringValue = String(value);
    if (stringValue.trim() === "") {
        return "";
    }

    const sanitizedValue = stringValue.replace(/[^0-9-]/g, "");
    if (sanitizedValue === "-") {
        return "-";
    }

    return sanitizedValue.replace(/(?!^)-/g, "");
}

function formatCentsToMoney(value) {
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
        return "";
    }

    return (parsedValue / 100).toFixed(2);
}

function applyBaseValueOffsets(fieldName, baseValue, attributeSelections, baseValueOffsets = {}) {
    let adjustedValue = baseValue;

    if (Number.isNaN(adjustedValue)) {
        return adjustedValue;
    }

    const fieldOffsets = baseValueOffsets[fieldName] || {};

    attributeSelections.forEach((selection) => {
        const attributeName = String(selection?.name || "").trim();
        const attributeValue = String(selection?.value || "").trim();

        if (!attributeName || !attributeValue) {
            return;
        }

        const offsetValue = fieldOffsets[attributeName]?.[attributeValue];
        if (offsetValue === undefined || offsetValue === null || String(offsetValue).trim() === "") {
            return;
        }

        if (fieldName === "price" || fieldName === "cost") {
            const parsedOffset = parseMoneyToCents(offsetValue);
            if (!Number.isNaN(parsedOffset)) {
                adjustedValue = Number(adjustedValue) + parsedOffset;
            }
            return;
        }

        const parsedOffset = Number(offsetValue);
        if (!Number.isNaN(parsedOffset)) {
            adjustedValue = Number(adjustedValue) + parsedOffset;
        }
    });

    return adjustedValue;
}

function buildSkuFromAttributeSelections(productName, attributeSelections) {
    const productNamePart = normalizeSkuPart(productName);
    const attributeValueParts = (Array.isArray(attributeSelections) ? attributeSelections : [])
        .map((selection) => normalizeSkuPart(selection?.value))
        .filter(Boolean);

    return [productNamePart, ...attributeValueParts]
        .filter(Boolean)
        .join("-");
}

function mapMatrixVariantToVariantPayload(matrixVariant) {
    const normalizedSku = String(matrixVariant?.sku || "").trim();
    const price = Number(matrixVariant?.price);
    const cost = Number(matrixVariant?.cost);
    const stock = Number(matrixVariant?.stock);
    const weight = Number(matrixVariant?.weight);
    const mediaItems = Array.isArray(matrixVariant?.media) ? matrixVariant.media : [];

    const normalizedImages = mediaItems
        .map((mediaItem, mediaIndex) => {
            const url = extractImageUrl(mediaItem);
            if (!url || !/^https?:\/\//i.test(url)) {
                return null;
            }

            const altText = String(
                mediaItem?.altText
                || mediaItem?.AltText
                || buildDefaultImageLabel(null, normalizedSku, mediaIndex + 1)
            ).trim();

            return {
                URL: url,
                AltText: altText,
            };
        })
        .filter(Boolean);

    const uploadImages = mediaItems
        .filter((mediaItem) => mediaItem?.file instanceof File)
        .map((mediaItem, mediaIndex) => ({
            file: mediaItem.file,
            altText: String(
                mediaItem?.altText
                || mediaItem?.AltText
                || buildDefaultImageLabel(mediaItem.file, normalizedSku, mediaIndex + 1)
            ).trim(),
        }));

    return {
        ID: 0,
        SKU: normalizedSku,
        Price: Number.isNaN(price) ? 0 : price,
        SalePrice: 0,
        EffectivePriceCents: Number.isNaN(price) ? 0 : price,
        Cost: Number.isNaN(cost) ? 0 : cost,
        Weight: Number.isNaN(weight) ? 0 : weight,
        Stock: Number.isNaN(stock) ? 0 : stock,
        Active: true,
        Attributes: (Array.isArray(matrixVariant?.attributeSelections) ? matrixVariant.attributeSelections : []).map((attributeSelection) => ({
            Name: String(attributeSelection?.name || "").trim(),
            Value: String(attributeSelection?.value || "").trim(),
        })),
        Images: normalizedImages,
        UploadImages: uploadImages,
    };
}

export function generateMatrixVariants(attributeValuesMap, baseVariantDefaults, baseValueOffsets = {}) {
    const attributeEntries = Object.entries(attributeValuesMap || {}).map(([attributeName, rawValues]) => {
        const uniqueValues = Array.from(new Set((Array.isArray(rawValues) ? rawValues : [])
            .map((value) => String(value ?? "").trim())
            .filter(Boolean)));

        return [attributeName, uniqueValues];
    }).filter(([, values]) => values.length > 0);

    if (attributeEntries.length === 0) {
        return [];
    }

    const combinations = attributeEntries.reduce((accumulator, [attributeName, values]) => {
        const nextCombinations = [];

        accumulator.forEach((combination) => {
            values.forEach((value) => {
                nextCombinations.push([
                    ...combination,
                    {
                        name: attributeName,
                        value,
                    },
                ]);
            });
        });

        return nextCombinations;
    }, [[]]);

    const seenSkus = new Set();
    const price = Number(baseVariantDefaults?.price);
    const cost = Number(baseVariantDefaults?.cost);
    const stock = parseNumberOrZero(baseVariantDefaults?.stock);
    const weight = parseNumberOrZero(baseVariantDefaults?.weight);

    return combinations.reduce((rows, attributeSelections, index) => {
        const sku = buildSkuFromAttributeSelections(baseVariantDefaults?.productName, attributeSelections);

        if (!sku || seenSkus.has(sku)) {
            return rows;
        }

        seenSkus.add(sku);

        const id = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `matrix-${index}-${sku}`;

        const adjustedPrice = applyBaseValueOffsets("price", price, attributeSelections, baseValueOffsets);
        const adjustedCost = applyBaseValueOffsets("cost", cost, attributeSelections, baseValueOffsets);
        const adjustedStock = applyBaseValueOffsets("stock", stock, attributeSelections, baseValueOffsets);
        const adjustedWeight = applyBaseValueOffsets("weight", weight, attributeSelections, baseValueOffsets);

        rows.push({
            id,
            sku,
            price: Number.isNaN(adjustedPrice) ? 0 : adjustedPrice,
            cost: Number.isNaN(adjustedCost) ? 0 : adjustedCost,
            stock: Number.isNaN(adjustedStock) ? 0 : adjustedStock,
            weight: Number.isNaN(adjustedWeight) ? 0 : adjustedWeight,
            selected: true,
            media: [],
            attributeSelections,
        });

        return rows;
    }, []);
}

export default function VariantMatrix({
    attributes = [],
    productName = "",
    onSaveVariantMatrix = () => {},
    onStepChange = () => {},
}) {

    const normalizedAttributes = React.useMemo(
        () => (Array.isArray(attributes) ? attributes : []),
        [attributes]
    );

    const [ attributeValues, setAttributeValues ] = React.useState(() => {
        return normalizedAttributes.reduce((acc, attribute) => {
            acc[attribute] = [];
            return acc;
        }, {});
    });

    React.useEffect(() => {
        setAttributeValues((previousValues) => {
            const nextValues = {};

            normalizedAttributes.forEach((attribute) => {
                nextValues[attribute] = Array.isArray(previousValues[attribute]) ? previousValues[attribute] : [];
            });

            return nextValues;
        });
    }, [normalizedAttributes]);


    const [ variantPrice, setVariantPrice ] = React.useState("");
    const [ variantCost, setVariantCost ] = React.useState("");
    const [ variantStock, setVariantStock ] = React.useState("");
    const [ variantWeight, setVariantWeight ] = React.useState("");
    const [ matrixVariants, setMatrixVariants ] = React.useState([]);
    const [ generationError, setGenerationError ] = React.useState("");
    const [ baseValueOffsets, setBaseValueOffsets ] = React.useState({
        price: {},
        cost: {},
        stock: {},
        weight: {},
    });
    const [ baseValueOffsetValuesOpen, setBaseValueOffsetValuesOpen ] = React.useState({
        price: false,
        cost: false,
        stock: false,
        weight: false,
    });
    const [ mediaUploadVariantId, setMediaUploadVariantId ] = React.useState("");
    const objectUrlsRef = React.useRef([]);

    const [ currentStep, setCurrentStep ] = React.useState(0);
    const matrixSteps = [
        "Enter Attribute Values",
        "Enter Base Values",
        "Review Variants"
    ];

    React.useEffect(() => {
        onStepChange?.(currentStep);
    }, [currentStep, onStepChange]);

    const revokeObjectUrls = React.useCallback(() => {
        objectUrlsRef.current.forEach((existingUrl) => {
            URL.revokeObjectURL(existingUrl);
        });
        objectUrlsRef.current = [];
    }, []);

    React.useEffect(() => {
        return () => {
            revokeObjectUrls();
        };
    }, [revokeObjectUrls]);

    const getMatrixMediaItemLabel = React.useCallback((mediaItem, variantSku, mediaIndex) => {
        const explicitName = String(mediaItem?.name || "").trim();
        if (explicitName) {
            return explicitName;
        }

        const explicitAltText = String(mediaItem?.altText || mediaItem?.AltText || "").trim();
        if (explicitAltText) {
            return explicitAltText;
        }

        if (mediaItem?.file instanceof File) {
            return buildDefaultImageLabel(mediaItem.file, variantSku, mediaIndex + 1);
        }

        const imageUrl = extractImageUrl(mediaItem);
        if (!imageUrl) {
            return buildDefaultImageLabel(null, variantSku, mediaIndex + 1);
        }

        const urlPath = imageUrl.split("?")[0];
        const lastPathSegment = urlPath.split("/").filter(Boolean).pop() || "";
        const decodedName = decodeURIComponent(lastPathSegment);
        const strippedName = stripFileExtension(decodedName);

        return strippedName || buildDefaultImageLabel(null, variantSku, mediaIndex + 1);
    }, []);

    const getVariantMediaDisplayItems = React.useCallback((variantMediaItems, variantSku) => {
        const mediaItems = Array.isArray(variantMediaItems) ? variantMediaItems : [];
        return mediaItems.map((mediaItem, mediaIndex) => ({
            index: mediaIndex,
            name: getMatrixMediaItemLabel(mediaItem, variantSku, mediaIndex),
        }));
    }, [getMatrixMediaItemLabel]);

    const handleRemoveMatrixMediaItem = React.useCallback((variantId, mediaIndexToRemove) => {
        setMatrixVariants((previousVariants) => previousVariants.map((variant) => {
            if (variant.id !== variantId) {
                return variant;
            }

            const existingMedia = Array.isArray(variant.media) ? variant.media : [];
            const mediaToRemove = existingMedia[mediaIndexToRemove];
            if (mediaToRemove?.source === "local" && typeof mediaToRemove?.url === "string") {
                URL.revokeObjectURL(mediaToRemove.url);
                objectUrlsRef.current = objectUrlsRef.current.filter((url) => url !== mediaToRemove.url);
            }

            return {
                ...variant,
                media: existingMedia.filter((_, mediaIndex) => mediaIndex !== mediaIndexToRemove),
            };
        }));
    }, []);

    const handleMatrixMediaUploadButtonClick = React.useCallback((variantId) => {
        setMediaUploadVariantId(String(variantId || ""));
        document.getElementById("matrixVariantMediaInput")?.click();
    }, []);

    const handleMatrixMediaFileChange = React.useCallback((event) => {
        const selectedFiles = Array.from(event?.target?.files || []).filter((file) => file instanceof File);
        if (selectedFiles.length === 0 || !mediaUploadVariantId) {
            if (event?.target) {
                event.target.value = "";
            }
            return;
        }

        setMatrixVariants((previousVariants) => previousVariants.map((variant) => {
            if (String(variant?.id || "") !== mediaUploadVariantId) {
                return variant;
            }

            const existingMedia = Array.isArray(variant.media) ? variant.media : [];
            const nextMedia = [...existingMedia];

            selectedFiles.forEach((file) => {
                const preview = createLocalImagePreview(file);
                if (!preview) {
                    return;
                }

                objectUrlsRef.current.push(preview.url);
                const nextMediaIndex = nextMedia.length + 1;
                const imageLabel = buildDefaultImageLabel(file, variant.sku, nextMediaIndex);

                nextMedia.push({
                    ...preview,
                    name: imageLabel,
                    altText: imageLabel,
                    isAltAuto: false,
                });
            });

            return {
                ...variant,
                media: nextMedia,
            };
        }));

        setMediaUploadVariantId("");
        if (event?.target) {
            event.target.value = "";
        }
    }, [mediaUploadVariantId]);

    const hasAttributeValues = React.useMemo(() => (
        normalizedAttributes.some((attribute) => Array.isArray(attributeValues[attribute]) && attributeValues[attribute].length > 0)
    ), [attributeValues, normalizedAttributes]);

    const handleMatrixAttributeItemsChange = React.useCallback((attribute, items) => {
        const nextItems = Array.isArray(items) ? items : [];

        setAttributeValues((previousValues) => {
            const previousItems = Array.isArray(previousValues[attribute]) ? previousValues[attribute] : [];
            const isSame = previousItems.length === nextItems.length
                && previousItems.every((itemValue, itemIndex) => itemValue === nextItems[itemIndex]);

            if (isSame) {
                return previousValues;
            }

            return {
                ...previousValues,
                [attribute]: nextItems,
            };
        });
    }, []);

    const matrixAttributeItemHandlers = React.useMemo(() => {
        return normalizedAttributes.reduce((handlers, attribute) => {
            handlers[attribute] = (items) => handleMatrixAttributeItemsChange(attribute, items);
            return handlers;
        }, {});
    }, [handleMatrixAttributeItemsChange, normalizedAttributes]);

    const canGenerateMatrix = React.useMemo(() => (
        hasAttributeValues
        && !Number.isNaN(parseMoneyToCents(variantPrice))
        && !Number.isNaN(parseMoneyToCents(variantCost))
        && !Number.isNaN(parseRequiredNumber(variantStock))
        && !Number.isNaN(parseRequiredNumber(variantWeight))
    ), [hasAttributeValues, variantCost, variantPrice, variantStock, variantWeight]);

    const matrixAttributeHeaders = React.useMemo(() => {
        const firstVariantAttributes = matrixVariants[0]?.attributeSelections;

        if (Array.isArray(firstVariantAttributes) && firstVariantAttributes.length > 0) {
            return firstVariantAttributes.map((attributeSelection) => attributeSelection.name);
        }

        return normalizedAttributes.filter((attribute) => Array.isArray(attributeValues[attribute]) && attributeValues[attribute].length > 0);
    }, [attributeValues, matrixVariants, normalizedAttributes]);

    const commitMatrixVariantAttribute = React.useCallback((variantId, attributeName, rawValue) => {
        const nextValue = String(rawValue ?? "").trim();

        setMatrixVariants((previousVariants) => previousVariants.map((variant) => {
            if (variant.id !== variantId) {
                return variant;
            }

            const nextAttributeSelections = Array.isArray(variant.attributeSelections)
                ? variant.attributeSelections.map((selection) => (
                    selection.name === attributeName
                        ? { ...selection, value: nextValue }
                        : selection
                ))
                : [];

            return {
                ...variant,
                sku: buildSkuFromAttributeSelections(productName, nextAttributeSelections),
                attributeSelections: nextAttributeSelections,
            };
        }));
    }, [productName]);

    React.useEffect(() => {
        setMatrixVariants((previousVariants) => previousVariants.map((variant) => ({
            ...variant,
            sku: buildSkuFromAttributeSelections(productName, variant.attributeSelections),
        })));
    }, [productName]);

    const commitMatrixVariantField = React.useCallback((variantId, fieldName, rawValue) => {
        setMatrixVariants((previousVariants) => previousVariants.map((variant) => {
            if (variant.id !== variantId) {
                return variant;
            }

            if (fieldName === "price" || fieldName === "cost" || fieldName === "stock" || fieldName === "weight") {
                const parsedValue = Number(rawValue);

                return {
                    ...variant,
                    [fieldName]: Number.isNaN(parsedValue) ? variant[fieldName] : parsedValue,
                };
            }

            return {
                ...variant,
                [fieldName]: rawValue,
            };
        }));
    }, []);

    const handleEditableCommit = React.useCallback((commitFn, ...args) => {
        commitFn(...args);
    }, []);

    function handleContinueClick() {
        if (hasAttributeValues) {
            setCurrentStep((previousStep) => Math.min(previousStep + 1, matrixSteps.length - 1));
        }
    }

    function handleGenerateMatrix() {
        const price = parseMoneyToCents(variantPrice);
        const cost = parseMoneyToCents(variantCost);
        const stock = parseRequiredNumber(variantStock);
        const weight = parseRequiredNumber(variantWeight);

        if (!hasAttributeValues) {
            setGenerationError("Add at least one attribute value before generating variants.");
            return;
        }

        if (Number.isNaN(price) || Number.isNaN(cost) || Number.isNaN(stock) || Number.isNaN(weight)) {
            setGenerationError("Enter valid base price, cost, stock, and weight values.");
            return;
        }

        if (!Number.isInteger(stock) || stock < 0) {
            setGenerationError("Base stock must be a non-negative whole number.");
            return;
        }

        const nextVariants = generateMatrixVariants(attributeValues, {
            productName,
            price,
            cost,
            stock,
            weight,
        }, baseValueOffsets);

        setMatrixVariants(nextVariants);
        setGenerationError("");
        setCurrentStep(2);
    }

    async function handleSaveSelected() {
        const selectedMatrixVariants = matrixVariants
            .filter((variant) => Boolean(variant?.selected));

        if (selectedMatrixVariants.length === 0) {
            setGenerationError("Select at least one variant to save.");
            return;
        }

        try {
            const selectedVariants = (await Promise.all(selectedMatrixVariants.map(async (matrixVariant) => {
                const mappedVariant = mapMatrixVariantToVariantPayload(matrixVariant);
                if (!mappedVariant?.SKU) {
                    return null;
                }

                const localUploadImages = Array.isArray(mappedVariant.UploadImages)
                    ? mappedVariant.UploadImages.filter((imageItem) => imageItem?.file instanceof File)
                    : [];

                const localPreviewImages = await Promise.all(localUploadImages.map(async (imageItem) => ({
                    URL: await readFileAsDataUrl(imageItem.file),
                    AltText: String(imageItem?.altText || "").trim(),
                })));

                return {
                    ...mappedVariant,
                    Images: [...(Array.isArray(mappedVariant.Images) ? mappedVariant.Images : []), ...localPreviewImages],
                };
            }))).filter((variant) => Boolean(variant?.SKU));

            if (selectedVariants.length === 0) {
                setGenerationError("Select at least one variant to save.");
                return;
            }

            onSaveVariantMatrix?.(selectedVariants);
            setGenerationError("");
        } catch (error) {
            setGenerationError(error?.message || "Failed to prepare matrix variant images.");
        }
    }

    const headerActions = [
        {
            label: "Continue",
            icon: <FaArrowRight size={18} />,
            onClick: handleContinueClick,
            disabled: !hasAttributeValues,
        },
        {
            label: "Generate Matrix",
            icon: <LuZap size={18} />,
            onClick: handleGenerateMatrix,
            disabled: !canGenerateMatrix,
        },
        {
            label: "Save Selected",
            icon: <LuSave size={18} />,
            onClick: handleSaveSelected,
            disabled: matrixVariants.every((variant) => !variant.selected),
        }
    ];

    function handleBaseValueOffsetChange(fieldName, attributeName, attributeValue, rawValue) {
        setBaseValueOffsets((previousOffsets) => ({
            ...previousOffsets,
            [fieldName]: {
                ...(previousOffsets[fieldName] || {}),
                [attributeName]: {
                    ...(previousOffsets[fieldName]?.[attributeName] || {}),
                    [attributeValue]: rawValue,
                },
            },
        }));
    }

    function renderBaseValueOffsetSection(fieldName, label, currentValue, onValueChange, placeholder, inputType = "number", step = "any") {
        const isIntegerField = fieldName === "stock";

        return (
            <div className="createVariantModalMatrixBaseValuesContainer">
                <p className="createVariantModalMatrixBaseValuesLabel">
                    {label}
                </p>
                <div className="createVariantModalMatrixBaseValuesInputContainer">
                    <Input
                        type={inputType}
                        value={currentValue}
                        onChange={(event) => onValueChange(isIntegerField ? sanitizeIntegerValue(event.target.value) : event.target.value)}
                        placeholder={placeholder}
                        step={step}
                        inputMode={isIntegerField ? "numeric" : undefined}
                        pattern={isIntegerField ? "[0-9]*" : undefined}
                    />
                    <button
                        type="button"
                        className="createVariantModalMatrixOffsetValuesButton"
                        onClick={() => setBaseValueOffsetValuesOpen((previousState) => ({
                            ...previousState,
                            [fieldName]: !previousState[fieldName],
                        }))}
                    >
                        {baseValueOffsetValuesOpen[fieldName] ? "Close" : "Offset Values"}
                    </button>
                </div>

                {baseValueOffsetValuesOpen[fieldName] && (
                    <div className="createVariantModalMatrixBaseValuesOffset">
                        {Object.entries(attributeValues).map(([attribute, values]) => (
                            (Array.isArray(values) ? values : []).map((value) => {
                                const fieldOffsets = baseValueOffsets[fieldName]?.[attribute] || {};

                                return (
                                    <div className="createVariantModalMatrixBaseValuesOffsetItem" key={`${fieldName}-${attribute}-${value}`}>
                                        {attribute}: {value}
                                        <Input
                                            type={inputType}
                                            value={fieldOffsets[value] ?? ""}
                                            onChange={(event) => handleBaseValueOffsetChange(fieldName, attribute, value, fieldName === "stock" ? sanitizeSignedIntegerValue(event.target.value) : event.target.value)}
                                            placeholder="0"
                                            step={fieldName === "price" || fieldName === "cost" ? "0.01" : "1"}
                                            inputMode={fieldName === "stock" ? "numeric" : undefined}
                                            pattern={fieldName === "stock" ? "[-0-9]*" : undefined}
                                            // styles={{ control: { backgroundColor: "transparent", border: "none"} }}
                                            numberPicker={{
                                                onDecrement: () => handleBaseValueOffsetChange(fieldName, attribute, value, fieldName === "stock"
                                                ? String(parseInt(fieldOffsets[value] ?? "0", 10) - 1)
                                                : String((parseFloat(fieldOffsets[value] ?? "0") - 1).toFixed(2))),
                                                onIncrement: () => handleBaseValueOffsetChange(fieldName, attribute, value, fieldName === "stock"
                                                ? String(parseInt(fieldOffsets[value] ?? "0", 10) + 1)
                                                : String((parseFloat(fieldOffsets[value] ?? "0") + 1).toFixed(2))),
                                            }}
                                        />
                                    </div>
                                );
                            })
                        ))}
                    </div>
                )}
            </div>
        );
    }

    function renderStepContent(step) {
        switch (step) {
            case 0:
                return (
                    <>
                        {normalizedAttributes.map((attribute) => (
                            <Input
                                key={attribute}
                                label={attribute}
                                type="addItem"
                                placeholder={`Enter ${attribute.toLowerCase()}`}
                                addItem={{
                                    items: attributeValues[attribute] || [],
                                    onItemsChange: matrixAttributeItemHandlers[attribute],
                                }}
                            />
                        ))}
                    </>
                );
            case 1:
                return (
                    <>
                        {renderBaseValueOffsetSection(
                            "price",
                            "Base Price",
                            variantPrice,
                            (value) => setVariantPrice(value),
                            "00.00",
                            "money",
                            "0.01"
                        )}
                        {renderBaseValueOffsetSection(
                            "cost",
                            "Base Cost",
                            variantCost,
                            (value) => setVariantCost(value),
                            "00.00",
                            "money",
                            "0.01"
                        )}
                        {renderBaseValueOffsetSection(
                            "stock",
                            "Base Stock",
                            variantStock,
                            (value) => setVariantStock(value),
                            "0",
                            "number",
                            "1"
                        )}
                        {renderBaseValueOffsetSection(
                            "weight",
                            "Base Weight (lbs)",
                            variantWeight,
                            (value) => setVariantWeight(value),
                            "0.00",
                            "number",
                            "0.01"
                        )}
                    </>
                );
            case 2:
                return (
                    <div className="createVariantModalMatrixPreviewContainer">
                        <div className="createVariantModalMatrixGridHeader">
                            <div className="createVariantModalMatrixHeaderCell createVariantModalMatrixGridCheckboxCell" role="columnheader" aria-label="selected"></div>
                            <div className="createVariantModalMatrixHeaderCell">SKU</div>
                            <div className="createVariantModalMatrixHeaderCell">Price</div>
                            <div className="createVariantModalMatrixHeaderCell">Cost</div>
                            <div className="createVariantModalMatrixHeaderCell">Stock</div>
                            <div className="createVariantModalMatrixHeaderCell">Weight (lbs)</div>
                            <div className="createVariantModalMatrixHeaderCell">Media</div>
                        </div>

                        <div className="createVariantModalMatrixGrid" aria-label="Generated variants matrix">
                            {matrixVariants.map((variant) => (
                                <React.Fragment key={variant.id}>
                                    <div className="createVariantModalMatrixGridCell createVariantModalMatrixGridCheckboxCell" role="cell">
                                        <input
                                            type="checkbox"
                                            checked={variant.selected}
                                            onChange={(event) => {
                                                const nextSelected = event.target.checked;
                                                setMatrixVariants((previousVariants) => previousVariants.map((currentVariant) => (
                                                    currentVariant.id === variant.id
                                                        ? { ...currentVariant, selected: nextSelected }
                                                        : currentVariant
                                                )));
                                            }}
                                        />
                                    </div>
                                    <div className="createVariantModalMatrixGridCell" role="cell">
                                        <input
                                            className="createVariantModalMatrixCellInput"
                                            type="text"
                                            value={variant.sku}
                                            readOnly={true}
                                            disabled={true}
                                            style={{ textAlign: 'left' }}
                                        />
                                    </div>
                                    <div className="createVariantModalMatrixGridCell" role="cell">
                                        $
                                        <input
                                            className="createVariantModalMatrixCellInput"
                                            type="number"
                                            defaultValue={formatCentsToMoney(variant.price)}
                                            onBlur={(event) => handleEditableCommit(commitMatrixVariantField, variant.id, "price", parseMoneyToCents(event.target.value))}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.currentTarget.blur();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="createVariantModalMatrixGridCell" role="cell">
                                        $
                                        <input
                                            className="createVariantModalMatrixCellInput"
                                            type="number"
                                            defaultValue={formatCentsToMoney(variant.cost)}
                                            onBlur={(event) => handleEditableCommit(commitMatrixVariantField, variant.id, "cost", parseMoneyToCents(event.target.value))}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.currentTarget.blur();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="createVariantModalMatrixGridCell" role="cell">
                                        <input
                                            className="createVariantModalMatrixCellInput"
                                            type="number"
                                            defaultValue={variant.stock}
                                            onBlur={(event) => handleEditableCommit(commitMatrixVariantField, variant.id, "stock", event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.currentTarget.blur();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="createVariantModalMatrixGridCell" role="cell">
                                        <input
                                            className="createVariantModalMatrixCellInput"
                                            type="number"
                                            defaultValue={variant.weight}
                                            onBlur={(event) => handleEditableCommit(commitMatrixVariantField, variant.id, "weight", event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.currentTarget.blur();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="createVariantModalMatrixGridCell createVariantModalMatrixMediaCell" role="cell">
                                        <div className="createVariantModalMatrixMediaSummary">
                                            {(() => {
                                                const mediaItems = getVariantMediaDisplayItems(variant.media, variant.sku);

                                                if (mediaItems.length === 0) {
                                                    return <span>No images</span>;
                                                }

                                                return (
                                                    <div className="createVariantModalMatrixMediaList">
                                                        {mediaItems.map((mediaItem) => (
                                                            <div className="createVariantModalMatrixMediaListItem" key={`${variant.id}-media-${mediaItem.index}-${mediaItem.name}`}>
                                                                <button
                                                                    type="button"
                                                                    className="createVariantModalMatrixMediaRemoveButton"
                                                                    onClick={() => handleRemoveMatrixMediaItem(variant.id, mediaItem.index)}
                                                                    aria-label={`Remove ${mediaItem.name}`}
                                                                    title={`Remove ${mediaItem.name}`}
                                                                >
                                                                    <LuX size={12} />
                                                                </button>
                                                                <span className="createVariantModalMatrixMediaName" title={mediaItem.name}>{mediaItem.name}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <button
                                            type="button"
                                            className="createVariantModalMatrixMediaUploadButton"
                                            onClick={() => handleMatrixMediaUploadButtonClick(variant.id)}
                                            aria-label={`Upload images for ${variant.sku}`}
                                            title={`Upload images for ${variant.sku}`}
                                        >
                                            <LuUpload size={16} />
                                        </button>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    }
    

    return (
        <div className="createVariantModalInnerContainer">
                <div className="createVariantModalHeaderContainer">
                    <h1>{matrixSteps[currentStep]}</h1>
                    <div className="createVariantModalActions">
                        {currentStep !== 0 ? (
                            <button
                                type="button"
                                className="btn createVariantModalCancelButton"
                                onClick={() => setCurrentStep(currentStep - 1)}
                            >
                                Back
                            </button>
                        ) : null}
                        <button 
                            type="button"
                            className="btn createVariantModalButton"
                            onClick={headerActions[currentStep].onClick}
                            disabled={headerActions[currentStep].disabled}
                        >
                            {headerActions[currentStep].label} {headerActions[currentStep].icon}
                        </button>
                    </div>
                </div>
                {generationError ? <p className="createVariantModalMatrixError">{generationError}</p> : null}
                {renderStepContent(currentStep)}
                <input
                    id="matrixVariantMediaInput"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleMatrixMediaFileChange}
                    style={{ display: "none" }}
                />
        </div>
    )
}
